// src/prototype/components/EAPersonalizationCard.tsx
// ============================================================
// EA PERSONALIZATION CARD
//
// STATUS, NOT A PERCENTAGE. The card used to render a completion ring with a number in
// the middle, driven by the Personalization Strength store. That store is gone, and with
// it the last thing that could produce a percentage here.
//
// The Documents card is why. Its number was a fraction over DEMO_DOCS_TOTAL, and that
// denominator had no correct value: three PDFs plus three workbooks is six, but six is
// not right either, because a count of documents measures nothing. There is no principled
// denominator, so the fraction is removed rather than repaired.
//
// What each card carries instead is a status and, where there is a real fact to state, a
// detail line. "3 documents uploaded" is a count of an actual list. "75%" was not.
// ============================================================

import React from 'react';
import { 
  Building2, 
  ClipboardList, 
  FileUp, 
  ChevronRight,
  CheckCircle2
} from 'lucide-react';
import { INTAKE_STATUS, type IntakeStatus } from '../../lib/coverage/intakeStatus';

export type PersonalizationInputType = 'profile' | 'surveys' | 'documents';

/**
 * Re-exported so existing importers keep working. THE definition lives in
 * lib/coverage/intakeStatus and this card does not add to it: one vocabulary, shared.
 */
export type PersonalizationStatus = IntakeStatus;

interface EAPersonalizationCardProps {
  type: PersonalizationInputType;
  status: IntakeStatus;
  /** The supporting line. A real, countable fact, never a percentage. */
  detail?: string;
  onClick?: () => void;
  className?: string;
  isLoading?: boolean;
}

const cardConfig: Record<PersonalizationInputType, {
  title: string;
  description: string;
  icon: React.ElementType;
  ctaLabels: Record<IntakeStatus, string>;
  emphasisLabel?: string;
  bulletItems: string[];
}> = {
  profile: {
    title: 'Organization Profile',
    description: 'Defines scale, constraints, and strategic posture used across all recommendations.',
    icon: Building2,
    ctaLabels: {
      not_started: 'Start Profile',
      in_progress: 'Continue Profile',
      complete: 'Review Profile',
      strengthened: 'Review Profile',
    },
    bulletItems: [
      'Organization basics',
      'Strategic priorities',
    ],
  },
  surveys: {
    title: 'Data Intake Surveys',
    description: 'Establishes priorities, capabilities, and readiness that drive ranking and ROI assumptions.',
    icon: ClipboardList,
    ctaLabels: {
      not_started: 'Start Surveys',
      in_progress: 'Continue Surveys',
      complete: 'Review Surveys',
      strengthened: 'Review Surveys',
    },
    emphasisLabel: 'Primary input for ranking & ROI',
    bulletItems: [
      'Strategy assessment',
      'Capabilities review',
      'Data readiness',
    ],
  },
  documents: {
    title: 'Document Upload',
    description: 'Optional, but significantly improves governance alignment and scoring accuracy.',
    icon: FileUp,
    ctaLabels: {
      not_started: 'Upload Documents',
      in_progress: 'Continue Upload',
      complete: 'Manage Documents',
      strengthened: 'Manage Documents',
    },
    bulletItems: [
      'Strategic plans',
      'Annual reports',
    ],
  },
};

/**
 * CARD CHROME ONLY. The badge's label and colours come from INTAKE_STATUS, so this table
 * cannot drift from the shared vocabulary or invent a fifth state. What lives here is the
 * card's own border and icon treatment, which is layout rather than meaning.
 */
const cardChrome: Record<IntakeStatus, { border: string; iconBg: string }> = {
  not_started: { border: 'border-[#e5e7eb]', iconBg: 'bg-[#F5F7FA]' },
  in_progress: { border: 'border-amber-200', iconBg: 'bg-amber-50' },
  complete: { border: 'border-green-200', iconBg: 'bg-green-50' },
  strengthened: { border: 'border-green-200', iconBg: 'bg-green-50' },
};

const CTA_CLASS = 'bg-[#18365E] text-white hover:bg-[#18365E]/90';

export function EAPersonalizationCard({ 
  type, 
  status,
  detail,
  onClick,
  className = '',
  isLoading = false
}: EAPersonalizationCardProps) {
  const config = cardConfig[type];
  const Icon = config.icon;
  const chrome = cardChrome[status];
  const badge = INTAKE_STATUS[status];

  return (
    <div
      onClick={onClick}
      className={`
        bg-white rounded-2xl border-2 p-5
        hover:shadow-lg cursor-pointer transition-all duration-300
        flex flex-col h-full
        ${chrome.border}
        ${className}
      `}
    >
      {/* Header with Icon and Status Badge */}
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${chrome.iconBg}`}>
          <Icon className="w-5 h-5 text-[#18365E]" />
        </div>
        
        <div className="flex items-center gap-1.5">
          {(status === 'complete' || status === 'strengthened') && (
            <CheckCircle2 className="w-4 h-4 text-green-600" />
          )}
          {/* The LABEL carries the whole meaning. Colour only supports it, so a reader who
              cannot separate the greens still knows which state this is. */}
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${badge.bgColor} ${badge.color} ${badge.borderColor}`}
          >
            {isLoading ? 'Loading...' : badge.label}
          </span>
        </div>
      </div>

      {/* Title with optional emphasis label */}
      <div className="mb-2">
        <h3 className="text-base font-bold text-[#18365E]">
          {config.title}
        </h3>
        {config.emphasisLabel && (
          <p className="text-[10px] font-medium text-[#717182]">
            {config.emphasisLabel}
          </p>
        )}
      </div>
      
      {/* Description */}
      <p className="text-xs text-[#717182] mb-4 flex-1 leading-snug">
        {config.description}
      </p>

      {/* Status detail and what this input covers. No ring, no percentage. */}
      <div className="flex items-start gap-3 mb-4">
        {isLoading ? (
          <div className="w-10 h-10 flex items-center justify-center shrink-0">
            <div className="animate-spin rounded-full h-6 w-6 border-4 border-[#e5e7eb] border-t-[#FF6E4C]"></div>
          </div>
        ) : (
          detail && (
            <p className="text-xs font-semibold text-[#18365E] shrink-0 max-w-[96px] leading-snug">
              {detail}
            </p>
          )
        )}

        <div className="flex-1 text-xs text-[#717182] space-y-0.5">
          {config.bulletItems.map((item, index) => (
            <p key={index}>• {item}</p>
          ))}
        </div>
      </div>

      {/* CTA Button */}
      <button
        className={`
          w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl
          text-sm font-semibold transition-all
          ${CTA_CLASS}
        `}
        disabled={isLoading}
      >
        {isLoading ? 'Loading...' : config.ctaLabels[status]}
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

export default EAPersonalizationCard;